import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Input from '../common/Input';
import Button from '../common/Button';
import CheckboxGroup from '../common/CheckboxGroup';
import MultiSelect from '../common/MultiSelect';
import LocationPicker from './LocationPicker';
import { validateForm } from '../../utils/validators';
import './GrowerForm.css';

const VALIDATION_RULES = {
  farm_name: { required: true, maxLength: 255 },
  region: { required: true, maxLength: 255 },
  email: { email: true },
  phone: {
    pattern: /^\+?1?\d{9,15}$/,
    patternMessage: 'Phone must be 9\u201315 digits, optionally prefixed with + or +1',
  },
  latitude: { number: true },
  longitude: { number: true },
};

const INITIAL_FORM = {
  farm_name: '',
  region: '',
  description: '',
  latitude: '',
  longitude: '',
  certifications: [],
  growing_practices: [],
  email: '',
  phone: '',
  product_ids: [],
};

/**
 * Django-admin-style grower form with fieldsets
 */
const GrowerForm = ({ grower, choices, onSave, onCancel, isSaving }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (grower) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        farm_name: grower.farm_name || '',
        region: grower.region || '',
        description: grower.description || '',
        latitude: grower.latitude ?? '',
        longitude: grower.longitude ?? '',
        certifications: grower.certifications || [],
        growing_practices: grower.growing_practices || [],
        email: grower.email || '',
        phone: grower.phone || '',
        product_ids: grower.products_detail
          ? grower.products_detail.map((p) => p.id)
          : [],
      });
    }
  }, [grower]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleLocationChange = (lat, lng) => {
    setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.latitude;
      delete next.longitude;
      return next;
    });
  };

  const handleArrayChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { isValid, errors: validationErrors } = validateForm(form, VALIDATION_RULES);
    if (!isValid) {
      // Flatten error arrays to first message string
      const flat = {};
      Object.entries(validationErrors).forEach(([k, v]) => {
        flat[k] = Array.isArray(v) ? v[0] : v;
      });
      setErrors(flat);
      return;
    }

    // Shape data for the API
    const payload = {
      farm_name: form.farm_name.trim(),
      region: form.region.trim(),
      description: form.description.trim(),
      latitude: form.latitude === '' ? null : Number(form.latitude),
      longitude: form.longitude === '' ? null : Number(form.longitude),
      certifications: form.certifications,
      growing_practices: form.growing_practices,
      email: form.email.trim(),
      phone: form.phone.trim(),
      product_ids: form.product_ids,
    };

    onSave(payload);
  };

  const certOptions = choices?.certifications || [];
  const practiceOptions = choices?.growing_practices || [];
  // Merge choices products with any products already on the grower
  // (handles edge case where a product was assigned before being deactivated)
  const choiceProducts = choices?.products || [];
  const growerProducts = grower?.products_detail || [];
  const productMap = new Map();
  choiceProducts.forEach((p) => productMap.set(p.id, p));
  growerProducts.forEach((p) => {
    if (!productMap.has(p.id)) productMap.set(p.id, p);
  });
  const productOptions = Array.from(productMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <form className="grower-form" onSubmit={handleSubmit} noValidate>
      {/* ── Fieldset 1: Farm Information ─────────────────── */}
      <fieldset className="grower-form__fieldset">
        <legend className="grower-form__legend">Farm Information</legend>
        <div className="grower-form__row">
          <Input
            label="Farm Name"
            name="farm_name"
            value={form.farm_name}
            onChange={handleChange}
            error={errors.farm_name}
            required
          />
          <Input
            label="Region"
            name="region"
            value={form.region}
            onChange={handleChange}
            error={errors.region}
            required
            placeholder="e.g., Middle Tennessee"
          />
        </div>
        <div className="grower-form__row grower-form__row--full">
          <div className="input-wrapper">
            <label htmlFor="grower-description" className="input-label">Description</label>
            <textarea
              id="grower-description"
              name="description"
              value={form.description}
              onChange={handleChange}
              className="input grower-form__textarea"
              rows={3}
              placeholder="Farm story, history, notable practices..."
            />
          </div>
        </div>
      </fieldset>

      {/* ── Fieldset 2: Location ─────────────────────────── */}
      <fieldset className="grower-form__fieldset">
        <legend className="grower-form__legend">Location</legend>
        <div className="grower-form__row">
          <Input
            label="Latitude"
            name="latitude"
            type="number"
            value={form.latitude}
            onChange={handleChange}
            error={errors.latitude}
            helperText="For map marker"
            placeholder="e.g., 35.9606"
          />
          <Input
            label="Longitude"
            name="longitude"
            type="number"
            value={form.longitude}
            onChange={handleChange}
            error={errors.longitude}
            helperText="For map marker"
            placeholder="e.g., -83.9207"
          />
        </div>
        <LocationPicker
          latitude={form.latitude}
          longitude={form.longitude}
          onChange={handleLocationChange}
        />
      </fieldset>

      {/* ── Fieldset 3: Certifications & Practices ──────── */}
      <fieldset className="grower-form__fieldset">
        <legend className="grower-form__legend">Certifications &amp; Practices</legend>
        <div className="grower-form__row grower-form__row--stacked">
          <CheckboxGroup
            label="Certifications"
            name="certifications"
            options={certOptions}
            value={form.certifications}
            onChange={handleArrayChange}
            error={errors.certifications}
            columns={2}
          />
          <CheckboxGroup
            label="Growing Practices"
            name="growing_practices"
            options={practiceOptions}
            value={form.growing_practices}
            onChange={handleArrayChange}
            error={errors.growing_practices}
            columns={2}
          />
        </div>
      </fieldset>

      {/* ── Fieldset 4: Contact ──────────────────────────── */}
      <fieldset className="grower-form__fieldset">
        <legend className="grower-form__legend">Contact</legend>
        <div className="grower-form__row">
          <Input
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            error={errors.email}
            placeholder="farmer@example.com"
          />
          <Input
            label="Phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            error={errors.phone}
            placeholder="6155551234"
          />
        </div>
      </fieldset>

      {/* ── Fieldset 5: Products ─────────────────────────── */}
      <fieldset className="grower-form__fieldset">
        <legend className="grower-form__legend">Products</legend>
        <MultiSelect
          label="Products this grower supplies grain for"
          name="product_ids"
          options={productOptions}
          value={form.product_ids}
          onChange={handleArrayChange}
          placeholder="Select products..."
        />
      </fieldset>

      {/* ── Actions ──────────────────────────────────────── */}
      <div className="grower-form__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : grower ? 'Save Changes' : 'Create Grower'}
        </Button>
      </div>
    </form>
  );
};

GrowerForm.propTypes = {
  grower: PropTypes.object,
  choices: PropTypes.shape({
    certifications: PropTypes.array,
    growing_practices: PropTypes.array,
    products: PropTypes.array,
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default GrowerForm;
